/**
 * Checkbox component based on shadcn/ui.
 * Uses native input for better test compatibility.
 */
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  checked?: boolean;
  indeterminate?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { className, checked = false, indeterminate = false, onCheckedChange, disabled, ...props },
    forwardedRef
  ) => {
    const innerRef = useRef<HTMLInputElement>(null);
    const ref = (forwardedRef as React.RefObject<HTMLInputElement>) || innerRef;

    // Handle indeterminate state
    useEffect(() => {
      if (ref && 'current' in ref && ref.current) {
        ref.current.indeterminate = indeterminate && !checked;
      }
    }, [indeterminate, checked, ref]);

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onCheckedChange?.(e.target.checked);
      },
      [onCheckedChange]
    );

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === ' ') {
          e.preventDefault();
          onCheckedChange?.(!checked);
        }
      },
      [checked, onCheckedChange]
    );

    const isIndeterminate = indeterminate && !checked;

    return (
      <div className="relative inline-flex items-center">
        <input
          type="checkbox"
          ref={ref}
          checked={checked}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="sr-only peer"
          {...props}
        />
        <button
          type="button"
          role="checkbox"
          aria-checked={isIndeterminate ? 'mixed' : checked}
          aria-label={props['aria-label']}
          tabIndex={disabled ? -1 : 0}
          disabled={disabled}
          onClick={() => onCheckedChange?.(!checked)}
          onKeyDown={(e) => {
            if (e.key === ' ') {
              e.preventDefault();
              onCheckedChange?.(!checked);
            }
          }}
          className={cn(
            'h-4 w-4 shrink-0 rounded-sm border border-gray-200 ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-200 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50',
            (checked || isIndeterminate) && 'bg-gray-900 text-gray-50 border-gray-900',
            className
          )}
        >
          <span className="flex items-center justify-center text-current">
            {isIndeterminate ? (
              <Minus className="h-3 w-3" />
            ) : checked ? (
              <Check className="h-3 w-3" />
            ) : null}
          </span>
        </button>
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
